import React, { useCallback, useEffect, useState } from 'react';
import {
  User,
  MessageCircle,
  BellRing,
  Smartphone,
  Sparkles,
  Radio,
  ChevronDown,
  Settings,
  Coffee,
  Clock,
  CircleOff,
  MinusCircle,
  Video,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { cn } from '../ui/utils';
import { toast } from 'sonner@2.0.3';
import { useNavigate } from 'react-router-dom';

export type PresenceStatus =
  | 'ACTIVE'
  | 'BREAK'
  | 'AWAY'
  | 'OFFLINE'
  | 'DO_NOT_DISTURB'
  | 'IN_MEETING';

type PresenceMeta = {
  value: PresenceStatus;
  label: string;
  hint: string;
  icon: LucideIcon;
  dot: string;
  ring: string;
  text: string;
  glow: string;
};

const PRESENCE_META: PresenceMeta[] = [
  {
    value: 'ACTIVE',
    label: 'Active',
    hint: 'Available for messages & pages',
    icon: User,
    dot: 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]',
    ring: 'from-emerald-400/80 via-emerald-500/40 to-emerald-400/80',
    text: 'text-emerald-300',
    glow: 'shadow-[0_0_20px_rgba(52,211,153,0.35)]',
  },
  {
    value: 'BREAK',
    label: 'On Break',
    hint: 'Back soon',
    icon: Coffee,
    dot: 'bg-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.7)]',
    ring: 'from-orange-400/80 via-orange-500/40 to-orange-400/80',
    text: 'text-orange-300',
    glow: 'shadow-[0_0_20px_rgba(251,146,60,0.35)]',
  },
  {
    value: 'AWAY',
    label: 'Away',
    hint: 'Stepped out — low urgency only',
    icon: Clock,
    dot: 'bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.7)]',
    ring: 'from-amber-400/80 via-amber-500/40 to-amber-400/80',
    text: 'text-amber-300',
    glow: 'shadow-[0_0_20px_rgba(251,191,36,0.35)]',
  },
  {
    value: 'OFFLINE',
    label: 'Offline',
    hint: 'Appear offline to the team',
    icon: CircleOff,
    dot: 'bg-slate-500 shadow-[0_0_8px_rgba(100,116,139,0.5)]',
    ring: 'from-slate-500/60 via-slate-600/30 to-slate-500/60',
    text: 'text-slate-400',
    glow: 'shadow-[0_0_12px_rgba(100,116,139,0.25)]',
  },
  {
    value: 'DO_NOT_DISTURB',
    label: 'Do Not Disturb',
    hint: 'Silence non-critical alerts',
    icon: MinusCircle,
    dot: 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.7)]',
    ring: 'from-red-500/80 via-red-600/40 to-red-500/80',
    text: 'text-red-300',
    glow: 'shadow-[0_0_20px_rgba(239,68,68,0.35)]',
  },
  {
    value: 'IN_MEETING',
    label: 'In Meeting',
    hint: 'Ping only if urgent',
    icon: Video,
    dot: 'bg-violet-500 shadow-[0_0_10px_rgba(139,92,246,0.7)]',
    ring: 'from-violet-500/80 via-violet-600/40 to-violet-500/80',
    text: 'text-violet-300',
    glow: 'shadow-[0_0_20px_rgba(139,92,246,0.35)]',
  },
];

const PRESENCE_BY_VALUE = Object.fromEntries(
  PRESENCE_META.map((p) => [p.value, p]),
) as Record<PresenceStatus, PresenceMeta>;

const VALID_PRESENCE = new Set(PRESENCE_META.map((p) => p.value));

/** Demo roster until team presence API exists — keeps UX shippable in production. */
const TEAM_QUICK: { id: string; name: string; title: string }[] = [
  { id: 'alex', name: 'Alex Chen', title: 'SRE' },
  { id: 'jordan', name: 'Jordan Lee', title: 'Backend' },
  { id: 'taylor', name: 'Taylor Morgan', title: 'On-call' },
];

const MESSAGE_CHIPS = [
  'On it — taking a look',
  'Need ~10 min',
  'In a meeting — back soon',
];

type NotifyPrefs = { push: boolean; sms: boolean; digest: boolean };

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) };
  } catch {
    return fallback;
  }
}

export interface SidebarProfileHubUser {
  id?: string;
  name?: string;
  email?: string;
}

interface SidebarProfileHubProps {
  user: SidebarProfileHubUser | null;
  variant: 'card' | 'compact';
  className?: string;
}

function parseStoredPresence(raw: string | null): PresenceStatus {
  if (raw && VALID_PRESENCE.has(raw as PresenceStatus)) {
    return raw as PresenceStatus;
  }
  return 'ACTIVE';
}

export function SidebarProfileHub({
  user,
  variant,
  className,
}: SidebarProfileHubProps) {
  const navigate = useNavigate();
  const userId = user?.id || user?.email || 'guest';
  const presenceKey = `coremonit_presence_${userId}`;
  const notifyKey = `coremonit_notify_prefs_${userId}`;
  const streakKey = `coremonit_quick_reply_streak_${userId}`;

  const [hubOpen, setHubOpen] = useState(false);
  const [presence, setPresence] = useState<PresenceStatus>(() => {
    try {
      return parseStoredPresence(localStorage.getItem(presenceKey));
    } catch {
      return 'ACTIVE';
    }
  });

  const [prefs, setPrefs] = useState<NotifyPrefs>({
    push: false,
    sms: false,
    digest: true,
  });

  const [peerId, setPeerId] = useState(TEAM_QUICK[0]?.id ?? 'alex');
  const [note, setNote] = useState('');
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    setPrefs(loadJson(notifyKey, { push: false, sms: false, digest: true }));
    try {
      const s = Number(localStorage.getItem(streakKey) || '0');
      if (Number.isFinite(s)) setStreak(Math.min(99, Math.max(0, s)));
    } catch {
      setStreak(0);
    }
  }, [notifyKey, streakKey, userId]);

  useEffect(() => {
    try {
      localStorage.setItem(presenceKey, presence);
    } catch {
      /* ignore */
    }
  }, [presence, presenceKey]);

  const persistPrefs = useCallback(
    (patch: Partial<NotifyPrefs>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        try {
          localStorage.setItem(notifyKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [notifyKey],
  );

  const bumpStreak = useCallback(() => {
    setStreak((s) => {
      const n = Math.min(99, s + 1);
      try {
        localStorage.setItem(streakKey, String(n));
      } catch {
        /* ignore */
      }
      return n;
    });
  }, [streakKey]);

  const meta = PRESENCE_BY_VALUE[presence];
  const displayName = user?.name || 'Admin User';
  const displayEmail = user?.email || 'admin@coremonit.com';

  const setPresenceWithToast = (value: PresenceStatus) => {
    const m = PRESENCE_BY_VALUE[value];
    setPresence(value);
    toast.message(`Status: ${m.label}`, { description: m.hint });
  };

  const sendQuickDm = () => {
    const peer = TEAM_QUICK.find((t) => t.id === peerId);
    if (!peer) return;
    if (!note.trim()) {
      toast.error('Add a short message');
      return;
    }
    bumpStreak();
    toast.success(`Sent to ${peer.name}`, {
      description:
        'Demo delivery: wire to your chat backend. Full history lives in Collaboration.',
    });
    setNote('');
    setHubOpen(false);
  };

  const enableBrowserPush = async () => {
    if (!('Notification' in window)) {
      toast.error('This browser does not support notifications');
      return;
    }
    const result = await Notification.requestPermission();
    if (result === 'granted') {
      persistPrefs({ push: true });
      toast.success('Desktop alerts enabled');
      try {
        new Notification('CoreMonit', {
          body: 'You will receive system & mention alerts in this browser.',
        });
      } catch {
        /* ignore */
      }
    } else {
      toast.message('Still blocked', {
        description:
          'Enable notifications in the browser site settings to get phone-style alerts on desktop.',
      });
    }
  };

  const avatarBlock = (
    <div className="relative shrink-0">
      <div
        className={cn(
          'absolute -inset-0.5 rounded-full bg-gradient-to-br opacity-80 blur-[2px] transition-all duration-500',
          meta.ring,
          meta.glow,
        )}
        aria-hidden
      />
      <div
        className={cn(
          'relative flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-slate-950/90 backdrop-blur-md sm:h-12 sm:w-12',
          variant === 'compact' && 'h-10 w-10',
        )}
      >
        <User className="h-5 w-5 text-cyan-100/90 sm:h-6 sm:w-6" />
      </div>
      <span
        className={cn(
          'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-slate-950/90 ring-1 ring-black/20',
          meta.dot,
        )}
        aria-hidden
      />
      <span
        className={cn(
          'absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-slate-900/80 bg-slate-950/90 sm:hidden',
        )}
        aria-hidden
      >
        <meta.icon className={cn('h-2.5 w-2.5', meta.text)} />
      </span>
    </div>
  );

  const statusDropdown = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-left text-xs font-medium backdrop-blur-md transition-all duration-200',
            'hover:border-cyan-400/30 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50',
            meta.text,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.dot)} />
          <span className="truncate">{meta.label}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side={variant === 'compact' ? 'right' : 'bottom'}
        className="z-[130] w-56 border border-cyan-500/20 bg-slate-950/95 p-1.5 shadow-2xl shadow-cyan-500/10 backdrop-blur-xl"
      >
        {PRESENCE_META.map((opt) => {
          const Icon = opt.icon;
          const selected = presence === opt.value;
          return (
            <DropdownMenuItem
              key={opt.value}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                selected
                  ? 'bg-cyan-500/15 text-foreground focus:bg-cyan-500/20'
                  : 'text-slate-300 focus:bg-white/5 focus:text-foreground',
              )}
              onClick={() => setPresenceWithToast(opt.value)}
            >
              <Icon className={cn('h-4 w-4 shrink-0', opt.text)} />
              <span className={cn('h-2 w-2 shrink-0 rounded-full', opt.dot)} />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected && <Check className="h-4 w-4 shrink-0 text-cyan-400" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const hubPanelContent = (
    <div className="space-y-0 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">{displayName}</p>
              <p className="text-xs text-muted-foreground">{displayEmail}</p>
            </div>
            {streak > 0 && (
              <div className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-200">
                <Sparkles className="h-3.5 w-3.5" />
                {streak} quick replies
              </div>
            )}
          </div>

          <Separator className="my-3 bg-white/10" />

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Radio className="h-3.5 w-3.5" />
              Presence
            </div>
            <div className="grid gap-1.5">
              {PRESENCE_META.map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPresenceWithToast(opt.value)}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition',
                      presence === opt.value
                        ? 'border-cyan-400/50 bg-cyan-500/10 text-foreground'
                        : 'border-white/10 bg-white/5 hover:bg-white/10',
                    )}
                  >
                    <Icon className={cn('h-4 w-4', opt.text)} />
                    <span className={cn('h-2 w-2 rounded-full', opt.dot)} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{opt.label}</span>
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">
                        {opt.hint}
                      </span>
                    </div>
                    {presence === opt.value && (
                      <Check className="h-4 w-4 shrink-0 text-cyan-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <Separator className="my-3 bg-white/10" />

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <MessageCircle className="h-3.5 w-3.5" />
              Ping a teammate
            </div>
            <p className="text-[11px] text-muted-foreground">
              Fast DM — opens Collaboration for full threads when you need history.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TEAM_QUICK.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setPeerId(t.id)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] transition',
                    peerId === t.id
                      ? 'border-cyan-400/70 bg-cyan-500/15 text-foreground'
                      : 'border-white/10 bg-white/5 hover:bg-white/10',
                  )}
                >
                  {t.name.split(' ')[0]}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {MESSAGE_CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="rounded-md bg-white/5 px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-white/10"
                  onClick={() => setNote(c)}
                >
                  {c}
                </button>
              ))}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Write a quick message…"
              className="w-full resize-none rounded-lg border border-white/10 bg-slate-900/60 px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                onClick={sendQuickDm}
              >
                Send
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-white/15"
                onClick={() => {
                  setHubOpen(false);
                  navigate('/team/chat');
                }}
              >
                Open chat
              </Button>
            </div>
          </div>

          <Separator className="my-3 bg-white/10" />

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <BellRing className="h-3.5 w-3.5" />
              Alerts on this device
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="cm-push" className="text-xs font-medium">
                  Browser push
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  System & mention style alerts in this browser.
                </p>
              </div>
              <Switch
                id="cm-push"
                checked={prefs.push}
                onCheckedChange={(v) => {
                  if (v) {
                    if (
                      typeof Notification !== 'undefined' &&
                      Notification.permission === 'granted'
                    ) {
                      persistPrefs({ push: true });
                      toast.success('Desktop alerts on');
                    } else {
                      void enableBrowserPush();
                    }
                  } else {
                    persistPrefs({ push: false });
                    toast.message('Desktop push off');
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="cm-sms" className="flex items-center gap-1 text-xs font-medium">
                  <Smartphone className="h-3 w-3" />
                  SMS (critical)
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  Preference saved here; add your number in Settings to go live.
                </p>
              </div>
              <Switch
                id="cm-sms"
                checked={prefs.sms}
                onCheckedChange={(v) => {
                  persistPrefs({ sms: v });
                  if (v) {
                    toast.success('SMS preference on', {
                      description:
                        'Add and verify your mobile under System → Settings to activate delivery.',
                    });
                  } else {
                    toast.message('SMS alerts off');
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="cm-digest" className="text-xs font-medium">
                  Daily digest email
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  One calm summary instead of constant pings.
                </p>
              </div>
              <Switch
                id="cm-digest"
                checked={prefs.digest}
                onCheckedChange={(v) => persistPrefs({ digest: v })}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs text-cyan-400 hover:text-cyan-300"
              onClick={() => {
                setHubOpen(false);
                navigate('/system/settings');
              }}
            >
              All notification settings →
            </Button>
          </div>
        </div>
  );

  const hubPanel = (
    <PopoverContent
      side="right"
      align="start"
      className="z-[120] w-[min(22rem,calc(100vw-2rem))] max-h-[min(85vh,32rem)] overflow-y-auto border-cyan-500/20 bg-slate-950/95 p-0 shadow-2xl shadow-cyan-500/10 backdrop-blur-xl"
      collisionPadding={12}
    >
      {hubPanelContent}
    </PopoverContent>
  );

  if (variant === 'compact') {
    return (
      <Popover open={hubOpen} onOpenChange={setHubOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'relative flex w-full items-center justify-center rounded-xl border border-cyan-500/20 bg-slate-900/40 p-2.5 backdrop-blur-md transition-all duration-300',
              'hover:border-cyan-400/40 hover:bg-slate-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50',
              className,
            )}
            aria-label={`${displayName}, ${meta.label}`}
          >
            {avatarBlock}
          </button>
        </PopoverTrigger>
        {hubPanel}
      </Popover>
    );
  }

  return (
    <>
      <div
        className={cn(
          'group relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-slate-900/70 via-slate-950/80 to-slate-950/90 p-3 shadow-lg shadow-cyan-500/5 backdrop-blur-xl transition-all duration-300',
          'hover:border-cyan-400/35 hover:shadow-cyan-500/10 sm:p-3.5',
          className,
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-violet-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          aria-hidden
        />

        <div className="relative flex items-start gap-2.5 sm:gap-3">
          {avatarBlock}

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-50">{displayName}</p>
            <p className="truncate text-xs text-slate-400">{displayEmail}</p>
            <div className="mt-2">{statusDropdown}</div>
          </div>

          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              title="Messages & quick DM"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-300 backdrop-blur-md transition-all duration-200 hover:border-cyan-400/30 hover:bg-cyan-500/10 hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
              onClick={() => setHubOpen(true)}
            >
              <MessageCircle className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Notification settings"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-300 backdrop-blur-md transition-all duration-200 hover:border-cyan-400/30 hover:bg-cyan-500/10 hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
              onClick={() => setHubOpen(true)}
            >
              <Settings className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <Popover open={hubOpen} onOpenChange={setHubOpen}>
        {hubPanel}
      </Popover>
    </>
  );
}
